'use client';

import { useState } from 'react';

import type { TDynamicData } from '../../types/dynamic-data';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { MemberZoneHeader } from './member-zone-header';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MinutesTab } from './tabs/minutes-tab';
import { MemberListTab } from './tabs/member-list-tab';
import { MarketStatisticsTab } from './tabs/market-statistics-tab';
import { RecievablesTab } from './tabs/recievables-tab';

import { GoArrowRight } from 'react-icons/go';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export const formattedDate = (item: TDynamicData) =>
  new Date(item.created_at)
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    .split('/')
    .join('.');

export function MemberZoneLogged() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const tabs = [
    {
      name: t('text2410'),
      content: <MinutesTab locale={locale} />,
    },
    {
      name: t('text2411'),
      content: <MemberListTab locale={locale} />,
    },
    {
      name: t('text2412'),
      content: <MarketStatisticsTab locale={locale} />,
    },
    {
      name: t('text2413'),
      content: <RecievablesTab locale={locale} />,
    },
  ];

  const [activeTab, setActiveTab] = useState<string>(tabs[0].name);

  return (
    <>
      <MemberZoneHeader />
      <Tabs
        onValueChange={setActiveTab}
        value={activeTab}
        defaultValue={activeTab}
        className={cn(
          'custom-section',
          'ea-member-zone__logged-tabs',
          'pb-10 pr-0 pt-16 sm:pb-12 sm:pt-14 md:px-5 md:pt-12 lg:pb-20 lg:pt-10',
        )}
      >
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-10 md:flex-row md:justify-between xl:gap-12',
          )}
        >
          <TabsList
            className={cn(
              'ea-member-zone__logged-tabs-list',
              'overflow-x-scroll md:overflow-x-hidden',
              'flex h-fit w-full flex-row items-start justify-start gap-0 bg-transparent p-[1px] shadow-none md:max-w-[250px] md:flex-col md:gap-2 lg:max-w-[357px]',
              'hide-scroll-bar',
            )}
          >
            {tabs.map(tab => (
              <TabsTrigger
                value={tab.name}
                key={tab.name}
                className={cn(
                  'ea-member-zone__logged-tabs-trigger',
                  'relative flex w-fit items-center justify-between gap-2 rounded-none border-b border-b-primary/50 p-4 text-left text-base tracking-tight shadow-none md:w-full md:text-wrap lg:text-lg',
                )}
              >
                {tab.name}
                <div className="absolute bottom-0 left-0 z-[5] h-[3px] w-full bg-primary" />
                <GoArrowRight size={20} className="hidden min-h-5 min-w-5 text-primary md:block" />
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent
            value={activeTab}
            className={cn(
              'ea-member-zone__logged-tabs-content',
              'flex w-full flex-col items-start justify-start gap-5 pr-5 sm:gap-8 md:max-w-[715px] md:pr-0 lg:gap-10',
            )}
          >
            <h3 className="text-2xl font-bold text-primary sm:text-3xl">
              {tabs.find(tab => tab.name === activeTab)?.name}
            </h3>
            {tabs.find(tab => tab.name === activeTab)?.content}
          </TabsContent>
        </div>
      </Tabs>
    </>
  );
}
